// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import TerceraBaseWindowTemplate from '@front/templates/TerceraBaseWindowTemplate.html';
import TypesManagerScreenTemplate from '@front/templates/TypesManagerScreenTemplate.html';
import TypesManagerScreenFooterTemplate from '@front/templates/TypesManagerScreenFooterTemplate.html';
import { DefaultLotTableItem } from '../cache/DefaultLotTableItem';
import { Control } from '../elements/Control';
import { QuickTableEditingInfo } from '../elements/QuickTable/QuickTableMisc';
import { TerceraWindowBase } from './TerceraWindowBase';
import { ThemeManager } from '../misc/ThemeManager';
import { DynProperty } from '@shared/commons/DynProperty';
import { SLTPTriggerUtils } from '@shared/commons/cache/OrderParams/SLTPTriggerUtils';
import { SLTPTrigger } from '@shared/utils/SlTpTrigger';
import { Instrument } from '@shared/commons/cache/Instrument';
import { DataCache } from '@shared/commons/DataCache';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { HtmlScroll } from '@components/webdesktop/elements/Scroll/HtmlScroll';
import { MathUtils } from '@shared/utils/MathUtils';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
export class TypesManagerScreen extends TerceraWindowBase {
    constructor() {
        super();
    }
    getType() {
        return PanelNames.TypesManagerScreen;
    }
    oninit() {
        super.oninit();
        this.observe('itemSettings', this.onItemSettingsChanged);
        this.observe('allSettings', this.update);
    }
    oncomplete() {
        super.oncomplete();
        this.addScrolls();
        void this.set({
            isAllowSLTPPercent: DataCache.IsAllowSLTPPercentForUser()
        });
        this.quickTableRactive = this.Controls.quickTableRactive;
        this.layoutTable();
        const columnsInfo = new DefaultLotTableItem(null);
        const qt = this.quickTableRactive.quickTable;
        qt.canShowHeaderMenu = false;
        qt.allowGroupBy = false;
        qt.lockManualSorting = true;
        qt.InitializeDirect(columnsInfo);
        qt.UpdateSortedColumns();
        qt.AfterEditItem.Subscribe(this.onAfterEditItem, this);
        this.localize();
        this.themeChange();
        ThemeManager.onThemeChange.Subscribe(this.themeChange, this);
        Control.Ticker.Subscribe(this.TickAsync, this);
        qt.needRedrawBackground = true;
        qt.needRedraw = true;
        if (this.deferredInstrumentToAdd) {
            this.Controls.lookup.AddInstrument(this.deferredInstrumentToAdd);
            delete this.deferredInstrumentToAdd;
        }
    }
    dispose() {
        this.removeScrolls();
        Control.Ticker.UnSubscribe(this.TickAsync, this);
        ThemeManager.onThemeChange.UnSubscribe(this.themeChange, this);
        delete this.caller;
        delete this.callBack;
        delete this.deferredInstrumentToAdd;
        MainWindowManager.TypesManagerScreen = null;
        super.dispose();
    }
    onItemSettingsChanged(itemSettings) {
        this.repopulateDefaultLots(itemSettings);
        this.repopulateSLTPTriggers(itemSettings);
    }
    repopulateDefaultLots(itemSettings) {
        const qt = this.quickTableRactive ? this.quickTableRactive.quickTable : null;
        if (!qt)
            return;
        qt.ClearAll();
        qt.BeginUpdate();
        const defaultLots = itemSettings.DefaultLots;
        const len = defaultLots.length;
        for (let i = 0; i < len; i++) {
            const defLot = defaultLots[i];
            const row = qt.AddItem(new DefaultLotTableItem(defLot, i));
            this.ProcessRow(row, defLot);
        }
        qt.EndUpdate();
        qt.needRedraw = true;
    }
    ProcessRow(row, defaultLot) {
        if (!row)
            return;
        const cells = row.cells;
        const selectedLotCell = cells[0];
        if (!selectedLotCell.QuickTableEditingInfo) {
            const info = new QuickTableEditingInfo(DynProperty.BOOLEAN);
            selectedLotCell.QuickTableEditingInfo = info;
            selectedLotCell.ReadOnly = false;
        }
        selectedLotCell.value = defaultLot.Selected;
        const lotValueCell = cells[1];
        if (!lotValueCell.QuickTableEditingInfo) {
            const info = new QuickTableEditingInfo(DynProperty.DOUBLE);
            info.Inc = info.Min = defaultLot.Increment;
            info.Max = 9999999;
            info.DecimalPlaces = defaultLot.Digits;
            lotValueCell.QuickTableEditingInfo = info;
            lotValueCell.ReadOnly = false;
        }
        const lots = defaultLot.Lots;
        lotValueCell.value = defaultLot.Lots;
        lotValueCell.formattedValue = TypesManagerScreen.getFormattedLotValue(lots);
    }
    static getFormattedLotValue(value) {
        return value.toString();
    }
    onAfterEditItem(params) {
        const row = params.row;
        if (!row)
            return;
        const itemSettings = this.get('itemSettings');
        const defaultLot = itemSettings.DefaultLots[row.id];
        const colIdx = params.columnIndex;
        switch (colIdx) {
            // Selected lot.
            case 0:
                defaultLot.Selected = !defaultLot.Selected;
                break;
            // Lot value.
            case 1:
                defaultLot.Lots = MathUtils.formatValueWithEps(params.newValue);
                break;
        }
        this.ProcessRow(row, defaultLot);
    }
    TickAsync() {
        const qtRactive = this.quickTableRactive;
        if (qtRactive === null || qtRactive === void 0 ? void 0 : qtRactive.quickTable.needRedraw) {
            const qt = qtRactive.quickTable;
            qt.needRedraw = false;
            qt.Draw();
        }
    }
    layoutTable() {
        if (this.quickTableRactive != null) {
            this.layoutTableResize(this.quickTableRactive);
        }
    }
    layoutTableResize(table) {
        table.resetSizes().then(() => {
            table.setSizes();
        });
    }
    localize() {
        var _a, _b;
        super.localize();
        (_b = (_a = this.quickTableRactive) === null || _a === void 0 ? void 0 : _a.quickTable) === null || _b === void 0 ? void 0 : _b.localize();
        void this.set('header', Resources.getResource('TypesManagerScreen.Types Manager'));
        void this.set({
            okBtnText: Resources.getResource('general.messageBox.ok'),
            okBtnTooltip: Resources.getResource('general.messageBox.ok.ToolTip'),
            cancelBtnText: Resources.getResource('general.messageBox.cancel'),
            cancelBtnTooltip: Resources.getResource('general.messageBox.cancel.ToolTip'),
            slDefaultText: Resources.getResource('TypesManagerScreen.NumericLabel.SL default offset, ticks'),
            tpDefaultText: Resources.getResource('TypesManagerScreen.NumericLabel.TP default offset, ticks'),
            slDefaultPercentText: Resources.getResource('TypesManagerScreen.NumericLabel.SL default offset, percent'),
            tpDefaultPercentText: Resources.getResource('TypesManagerScreen.NumericLabel.TP default offset, percent'),
            usrQtyText: Resources.getResource('TypesManagerScreen.NumericLabel.User quantity coefficient'),
            priceIncrementText: Resources.getResource('TypesManagerScreen.NumericLabel.Price increment (arrow), ticks'),
            defLotsSetupText: Resources.getResource('TypesManagerScreen.DefaultLotsSetup')
        });
        if (this.quickTableRactive) {
            this.quickTableRactive.quickTable.localize();
        }
    }
    themeChange() {
        if (this.quickTableRactive) {
            this.quickTableRactive.themeChange();
        }
        const lookup = this.Controls.lookup;
        if (lookup) {
            lookup.themeChange();
        }
    }
    okClick() {
        if (this.callBack) {
            this.callBack(this.get('allSettings'));
        }
        this.close();
    }
    onCloseButtonClick() {
        if (this.callBack) {
            this.callBack(null);
        }
        this.close();
    }
    AddInstrument(instrument) {
        if (!instrument || !(instrument instanceof Instrument)) {
            return;
        }
        const lookup = this.Controls.lookup;
        if (lookup) {
            lookup.AddInstrument(instrument);
        }
        else {
            this.deferredInstrumentToAdd = instrument;
        }
    }
    repopulateSLTPTriggers(itemSettings) {
        var _a, _b;
        if (!itemSettings) {
            return;
        }
        const DC = DataCache;
        const ins = DC.getInstrumentByName(itemSettings.InstrumentName);
        const insRule = ins
            ? ins.IsAllowCustomSLTPTriggerPrice
            : DC.AllowCustomSLTPTriggerPriceForInstrumentType(itemSettings.InstrumentType);
        const userRule = DC.AllowCustomSLTPTriggerPriceForUser();
        const triggersVisible = userRule && insRule;
        void this.set('triggersVisible', triggersVisible);
        this.layoutTable(); // #120102
        if (triggersVisible && !itemSettings.sltpTriggerShort && itemSettings.sltpTriggerShort !== 0) {
            const tr = new SLTPTrigger();
            if ((_b = (_a = this.caller) === null || _a === void 0 ? void 0 : _a.ownerPanel) === null || _b === void 0 ? void 0 : _b.arrayProperties) {
                SLTPTriggerUtils.ApplyProperties(tr, this.caller.ownerPanel.arrayProperties);
            } // если поменяли перед открітіем тайм менеджера но не сохранили
            else {
                tr.CopyValueFrom(SessionSettings.GetTriggers());
            }
            void this.set('itemSettings.sltpTriggerShort', tr.GetShort());
        }
    }
    addScrolls() {
        if (!this.append) {
            return;
        }
        const rP = this.find('.js-right-panel');
        if (rP != null) {
            this.scroll = HtmlScroll.addScroll(rP);
        }
    }
    removeScrolls() {
        var _a;
        (_a = this.scroll) === null || _a === void 0 ? void 0 : _a.destroy();
    }
    static show(caller, instrumentToAdd, callBack) {
        var _a, _b;
        const scr = TypesManagerScreen.getInstance();
        scr.setFocus();
        scr.callBack = callBack;
        if (scr.caller !== caller) {
            scr.caller = caller;
            const settings = (_b = (_a = caller === null || caller === void 0 ? void 0 : caller.getAllInstrumentsDefaultSettings) === null || _a === void 0 ? void 0 : _a.call(caller)) !== null && _b !== void 0 ? _b : null;
            if (!isNullOrUndefined(settings) && !isNullOrUndefined(instrumentToAdd)) {
                settings.CreateInstrumentSettingsIfNotExist(instrumentToAdd);
                scr.AddInstrument(instrumentToAdd);
            }
            void scr.set({ allSettings: settings });
        }
    }
    static getInstance() {
        const instance = MainWindowManager.TypesManagerScreen;
        if (instance) {
            return instance;
        }
        const scr = new TypesManagerScreen();
        MainWindowManager.AddControl(scr);
        scr.center();
        MainWindowManager.TypesManagerScreen = scr;
        return scr;
    }
}
TerceraWindowBase.extendWith(TypesManagerScreen, {
    data: function () {
        return {
            width: 706,
            height: 684,
            resizable: false,
            allSettings: null,
            itemSettings: null,
            triggersVisible: false,
            isAllowSLTPPercent: false,
            sltpStep: 1,
            sltpMinValue: 1,
            sltpDecimalPrecision: 0,
            style_addition_body: 'js-types-ManagerScreen-AdditionalBody',
            style_addition_footer: 'js-types-ManagerScreen-AdditionalFooter'
        };
    },
    template: TerceraBaseWindowTemplate,
    partials: {
        bodyPartial: TypesManagerScreenTemplate,
        footerPartial: TypesManagerScreenFooterTemplate
    }
});
